import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { CORE_API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${CORE_API_BASE_URL}/clients`;

export default function useClientIDs(page = 0, pageSize = 10) {
  const authToken = sessionStorage.getItem("authToken");
  const [clientIDs, setClientIDs] = useState([]);
  const [selectedID, setSelectedID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [clientStats, setClientStats] = useState({
    ACTIVE: 0,
    RATE_LIMITED: 0,
    THROTTLED: 0,
    SUSPENDED: 0,
    BLOCKED: 0,
  });

  // handle axios errors
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message = "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  /** -------------------- Table Fetch -------------------- **/
  const fetchClientIDs = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const tableUrl = `${API_BASE_URL}?page=${page}&size=${pageSize}`;
      const tableRes = await apiClient.get(tableUrl);

      // Handle Table Data
      const {
        content = [],
        totalPages: tableTotalPages = 1,
        totalElements: tableTotalElements = 0,
      } = tableRes.data;

      setClientIDs(content.map(formatClientData));
      setTotalPages(tableTotalPages);
      setTotalElements(tableTotalElements);

    } catch (err) {
      handleAxiosErrors(err, "fetch client data");
    } finally {
      setLoading(false);
    }
  }, [authToken, page, pageSize]);

  /** -------------------- Search -------------------- **/
  const searchClients = useCallback(async (query, status) => {
    if (!authToken) return;
    setLoading(true);
    try {
      const BASE_URL = `/api/v1/clients`;
      const trimmedQuery = query?.trim() || "";
      const params = new URLSearchParams();
      
      if (status && status !== "ALL") params.append("status", status);
      if (trimmedQuery.length >= 3) params.append("q", trimmedQuery);

      const isFiltered = params.has("status") || params.has("q");
      const endpoint = isFiltered ? `${BASE_URL}/search` : BASE_URL;
      
      const queryString = params.toString();
      const finalUrl = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await apiClient.get(finalUrl);
      const { content = [], totalPages = 1, totalElements = 0 } = response.data;

      setClientIDs(content.map(formatClientData));
      setTotalPages(totalPages);
      setTotalElements(totalElements);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  /** -------------------- Stats Fetch (Filtered) -------------------- **/
  const fetchClientStats = useCallback(
    async (query = "") => {
      if (!authToken) return;

      try {
        const trimmedQuery = String(query || "").trim();
        const useQuery = trimmedQuery.length >= 3;
        const statuses = [
          "ACTIVE",
          "RATE_LIMITED",
          "THROTTLED",
          "SUSPENDED",
          "BLOCKED",
        ];

        const requests = statuses.map(async (status) => {
          const params = new URLSearchParams();
          params.append("status", status);
          params.append("page", "0");
          params.append("size", "1");
          if (useQuery) {
            params.append("q", trimmedQuery);
          }

          const response = await apiClient.get(
            `/api/v1/clients/search?${params.toString()}`
          );
          return [status, response?.data?.totalElements || 0];
        });

        const entries = await Promise.all(requests);
        const stats = {
          ACTIVE: 0,
          RATE_LIMITED: 0,
          THROTTLED: 0,
          SUSPENDED: 0,
          BLOCKED: 0,
        };
        entries.forEach(([status, count]) => {
          stats[status] = count;
        });
        setClientStats(stats);
      } catch (err) {
        console.error("fetch client stats error:", err);
      }
    },
    [authToken]
  );

  /** -------------------- Single Client Operations -------------------- **/
  const fetchClientIDById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`${API_BASE_URL}/${id}`);
      setSelectedID(res.data);
      return res.data;
    } catch (err) {
      handleAxiosErrors(err, "fetch client ID details");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const updateClientID = useCallback(async (id, updatedData) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.put(`${API_BASE_URL}/${id}`, updatedData);
      const formatted = formatClientData(res.data);

      setClientIDs((prev) => prev.map((key) => (key.id === id ? formatted : key)));
      setSelectedID((prev) => (prev && prev.id === id ? res.data : prev));

      toast.success("Client ID updated successfully!", { theme: "colored" });
      return res.data;
    } catch (err) {
      handleAxiosErrors(err, "Update client ID");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Initial load
  useEffect(() => {
    if (authToken) fetchClientIDs();
  }, [authToken, fetchClientIDs]);

  return {
    clientIDs,
    selectedID,
    clientStats,
    loading,
    error,
    fetchClientIDs,
    fetchClientStats,
    searchClients,
    fetchClientIDById,
    updateClientID,
    setSelectedID,
    totalPages,
    totalElements,
  };
}

const formatClientData = (client = {}) => {
  return {
    id: client.id || null,
    apiKeyId: client.apiKeyId || null,
    status: client.status || "UNKNOWN",
    createdAt: client.createdAt || null,
    ...client 
  };
};
